import { useCallback, useMemo, useState } from 'react';

import { useLazyQuery } from '@apollo/client/react';
import { ListItem, ListItemText, debounce } from '@mui/material';
import { grey } from '@mui/material/colors';
import { Search } from 'lucide-react';

import { RaAutoComplete } from '@modules/_shared/components/data-grid/RaAutoComplete';
import {
  SEARCH_CLOSE_LEADS,
  SEARCH_STRIPE_CUSTOMERS,
} from '@modules/organisations/organisationsQueries';

type SourceSystemResult = {
  id: string;
  name?: string | null;
  email?: string | null;
  status?: string | null;
  url: string;
};

type SourceSystemSearchProps = {
  source: 'stripe' | 'close';
  placeholder: string;
  onSelect: (result: SourceSystemResult) => void;
  /** Excludes candidates already linked to other orgs (this org stays visible). */
  organisationId?: string;
};

// Manual fallback search for Stripe customers / Close leads, used when the
// automatic suggestions miss. Pure autocomplete: the scoring/recommendation
// lives in MatchSuggestionField.
export const SourceSystemSearch = ({
  source,
  placeholder,
  onSelect,
  organisationId,
}: SourceSystemSearchProps) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<SourceSystemResult[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [searchStripeCustomers, stripeQuery] = useLazyQuery(
    SEARCH_STRIPE_CUSTOMERS,
    { notifyOnNetworkStatusChange: true },
  );
  const [searchCloseLeads, closeQuery] = useLazyQuery(SEARCH_CLOSE_LEADS, {
    notifyOnNetworkStatusChange: true,
  });
  const loading = stripeQuery.loading || closeQuery.loading;
  const error = stripeQuery.error || closeQuery.error;

  const runSearch = useCallback(
    async (query: string) => {
      const trimmed = query.trim();
      if (trimmed.length < 2) {
        setOptions([]);
        setOpen(false);
        return;
      }
      const variables = { query: trimmed, limit: 10, organisationId };
      const results =
        source === 'stripe'
          ? (await searchStripeCustomers({ variables })).data
              ?.search_stripe_customers?.data
          : (await searchCloseLeads({ variables })).data?.search_close_leads
              ?.data;
      setOptions((results ?? []) as SourceSystemResult[]);
      setOpen(true);
    },
    [searchCloseLeads, searchStripeCustomers, source, organisationId],
  );

  const search = useMemo(() => debounce(runSearch, 300), [runSearch]);

  const selectResult = (result: SourceSystemResult) => {
    onSelect(result);
    setInputValue('');
    setOpen(false);
    setOptions([]);
  };

  return (
    <RaAutoComplete<SourceSystemResult>
      open={open}
      setOpen={setOpen}
      setOptions={setOptions}
      options={options}
      loading={loading}
      error={error != null}
      value={null}
      inputValue={inputValue}
      onInputChange={(_event, value, reason) => {
        if (reason === 'input' || reason === 'clear') {
          setInputValue(value);
        }
      }}
      debouncedSearch={search}
      InputProps={{
        startAdornment: (
          <Search size={18} style={{ color: grey[700], marginLeft: 4 }} />
        ),
        placeholder,
      }}
      getOptionLabel={option =>
        [option.name, option.email ?? option.status, option.id]
          .filter(Boolean)
          .join(' · ')
      }
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onChange={(_event, value) => {
        if (value != null) {
          selectResult(value);
        }
      }}
      renderOption={(props, option) => (
        <ListItem {...props} key={option.id} sx={{ py: 0.75 }}>
          <ListItemText
            primary={option.name ?? option.id}
            secondary={[option.email ?? option.status, option.id]
              .filter(Boolean)
              .join(' · ')}
            slotProps={{
              primary: { sx: { fontSize: '0.875rem' } },
              secondary: { sx: { fontSize: '0.75rem' } },
            }}
          />
        </ListItem>
      )}
    />
  );
};
